import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import MonkeyRideCanvas from './components/MonkeyRideCanvas';
import GameHUD from './components/GameHUD';
import MobileControls from './components/MobileControls';
import StartScreen from './components/StartScreen';
import GameOverScreen from './components/GameOverScreen';
import PauseScreen from './components/PauseScreen';
import ShopScreen from './components/ShopScreen';
import MissionsScreen from './components/MissionsScreen';
import LeaderboardScreen from './components/LeaderboardScreen';
import { GAME_STATUS, loadHighScore, saveHighScore, loadCoinBalance, saveCoinBalance, loadMissions, saveMissions, saveWeeklyScore, loadWeeklyScore, updateLeaderboard, loadSelectedCharacter, loadSelectedVehicle, loadSelectedEnvironment,  } from './engine/GameState';
import { ENVIRONMENTS, CHARACTERS } from './engine/GameEngine';

export default function MonkeyRide() {
  const navigate = useNavigate();
  const [gameStatus, setGameStatus] = useState(GAME_STATUS?.IDLE);
  const [score, setScore] = useState(0);
  const [coins, setCoins] = useState(0);
  const [distance, setDistance] = useState(0);
  const [highScore, setHighScore] = useState(loadHighScore);
  const [coinBalance, setCoinBalance] = useState(loadCoinBalance);
  const [multiplier, setMultiplier] = useState(1);
  const [activePowerUps, setActivePowerUps] = useState({});
  const [comboChain, setComboChain] = useState(0);
  const [selectedEnv, setSelectedEnv] = useState(loadSelectedEnvironment);
  const [selectedChar, setSelectedChar] = useState(loadSelectedCharacter);
  const [selectedVehicle, setSelectedVehicle] = useState(loadSelectedVehicle);
  const [missions, setMissions] = useState(loadMissions);
  const [completedMissions, setCompletedMissions] = useState([]);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [gameKey, setGameKey] = useState(0);

  // Overlay screens
  const [showShop, setShowShop] = useState(false);
  const [showMissions, setShowMissions] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const canvasInputRef = useRef(null);

  const gameStateObj = { status: gameStatus };

  const handleStart = useCallback(() => {
    setScore(0);
    setCoins(0);
    setDistance(0);
    setMultiplier(1);
    setActivePowerUps({});
    setComboChain(0);
    setCompletedMissions([]);
    setIsNewHighScore(false);
    setGameKey(k => k + 1);
    setGameStatus(GAME_STATUS?.PLAYING);
  }, []);

  const handleScoreUpdate = useCallback((newScore) => {
    setScore(newScore);
    // Update missions
    setMissions(prev => {
      const updated = prev?.map(m => {
        if (m?.completed) return m;
        if (m?.type === 'score' && newScore >= m?.target) {
          return { ...m, progress: newScore, completed: true };
        }
        return { ...m, progress: m?.type === 'score' ? newScore : m?.progress };
      });
      return updated;
    });
  }, []);

  const handleCoinsUpdate = useCallback((newCoins) => {
    setCoins(newCoins);
    setMissions(prev => prev?.map(m => {
      if (m?.completed || m?.type !== 'coins') return m;
      const progress = newCoins;
      return { ...m, progress, completed: progress >= m?.target };
    }));
  }, []);

  const handleDistanceUpdate = useCallback((newDist) => {
    setDistance(newDist);
    setMissions(prev => prev?.map(m => {
      if (m?.completed || m?.type !== 'distance') return m;
      return { ...m, progress: newDist, completed: newDist >= m?.target };
    }));
  }, []);

  const handlePowerUpCollect = useCallback((puId, powerUp) => {
    setActivePowerUps(prev => ({ ...prev, [puId]: Date.now() + powerUp?.duration }));
    if (puId === 'multiplier') setMultiplier(2);
    setMissions(prev => prev?.map(m => {
      if (m?.completed || m?.type !== 'powerups') return m;
      const progress = m?.progress + 1;
      return { ...m, progress, completed: progress >= m?.target };
    }));
  }, []);

  const handleCrash = useCallback(() => {
    // Brief visual feedback
  }, []);

  const handleGameOver = useCallback(() => {
    setGameStatus(GAME_STATUS?.GAME_OVER);

    // Save high score
    const finalScore = score;
    if (finalScore > highScore) {
      setHighScore(finalScore);
      saveHighScore(finalScore);
      setIsNewHighScore(true);
      updateLeaderboard('You', finalScore);
    }

    // Save weekly score
    const weekly = loadWeeklyScore();
    if (finalScore > weekly?.score) saveWeeklyScore(finalScore);

    // Add earned coins to balance
    const newBalance = coinBalance + coins;
    setCoinBalance(newBalance);
    saveCoinBalance(newBalance);

    // Check completed missions and award rewards
    const completed = missions?.filter(m => m?.completed && m?.progress >= m?.target);
    let bonusCoins = 0;
    completed?.forEach(m => { bonusCoins += m?.reward; });
    if (bonusCoins > 0) {
      const finalBalance = newBalance + bonusCoins;
      setCoinBalance(finalBalance);
      saveCoinBalance(finalBalance);
    }
    setCompletedMissions(completed);
    saveMissions(missions);
  }, [score, highScore, coinBalance, coins, missions]);

  const handleContinue = useCallback(() => {
    setGameStatus(GAME_STATUS?.PLAYING);
  }, []);

  const handleRestart = useCallback(() => {
    setMissions(loadMissions());
    handleStart();
  }, [handleStart]);

  const handlePause = useCallback(() => {
    setGameStatus(prev => prev === GAME_STATUS?.PAUSED ? GAME_STATUS?.PLAYING : GAME_STATUS?.PAUSED);
  }, []);

  // Keyboard pause
  useEffect(() => {
    const onKey = (e) => {
      if ((e?.code === 'KeyP' || e?.code === 'Escape') && gameStatus === GAME_STATUS?.PLAYING) {
        handlePause();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gameStatus, handlePause]);

  const charEmoji = CHARACTERS?.find(c => c?.id === selectedChar)?.emoji || '🐒';
  const envName = ENVIRONMENTS?.[selectedEnv]?.name || 'Jungle Road';

  const isPlaying = gameStatus === GAME_STATUS?.PLAYING;
  const isPaused = gameStatus === GAME_STATUS?.PAUSED;
  const isGameOver = gameStatus === GAME_STATUS?.GAME_OVER;
  const isIdle = gameStatus === GAME_STATUS?.IDLE;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        {/* Top bar */}
        <div className="sticky top-16 z-40 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all hover:bg-[var(--color-muted)]"
              style={{ color: 'var(--color-muted-foreground)' }}
            >
              <Icon name="ArrowLeft" size={16} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex-1">
              <h1 className="font-heading font-bold text-sm md:text-base" style={{ color: 'var(--color-foreground)' }}>
                🐒 Monkey Ride 3D
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {isPlaying && (
                <button
                  onClick={handlePause}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all"
                  style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--color-primary)', border: '1px solid rgba(99,102,241,0.3)' }}
                >
                  <Icon name="Pause" size={14} />
                  <span className="hidden sm:inline">Pause</span>
                </button>
              )}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <span className="text-sm">🪙</span>
                <span className="font-data text-sm font-bold" style={{ color: '#f59e0b' }}>{coinBalance?.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-4">
          {/* Game area */}
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              border: '2px solid rgba(99,102,241,0.4)',
              boxShadow: '0 0 40px rgba(99,102,241,0.2)',
              background: '#0a0a1a',
            }}
          >
            {/* Canvas */}
            {(isPlaying || isPaused) && (
              <>
                <MonkeyRideCanvas
                  key={gameKey}
                  gameState={gameStateObj}
                  onScoreUpdate={handleScoreUpdate}
                  onCoinsUpdate={handleCoinsUpdate}
                  onDistanceUpdate={handleDistanceUpdate}
                  onGameOver={handleGameOver}
                  onPowerUpCollect={handlePowerUpCollect}
                  onCrash={handleCrash}
                  isPaused={isPaused}
                  characterId={selectedChar}
                  vehicleId={selectedVehicle}
                  environmentKey={selectedEnv}
                />
                <GameHUD
                  score={score}
                  coins={coins}
                  distance={distance}
                  highScore={highScore}
                  multiplier={multiplier}
                  activePowerUps={activePowerUps}
                  comboChain={comboChain}
                  environment={envName}
                  characterEmoji={charEmoji}
                />
              </>
            )}

            {/* Start screen */}
            {isIdle && (
              <StartScreen
                onStart={handleStart}
                onOpenShop={() => setShowShop(true)}
                onOpenMissions={() => setShowMissions(true)}
                onOpenLeaderboard={() => setShowLeaderboard(true)}
                selectedEnv={selectedEnv}
                onEnvChange={setSelectedEnv}
              />
            )}

            {/* Pause overlay */}
            {isPaused && (
              <PauseScreen
                onResume={handlePause}
                onRestart={handleRestart}
                onGoHome={() => setGameStatus(GAME_STATUS?.IDLE)}
                score={score}
                coins={coins}
              />
            )}

            {/* Game over overlay */}
            {isGameOver && (
              <GameOverScreen
                score={score}
                coins={coins}
                distance={distance}
                highScore={highScore}
                isNewHighScore={isNewHighScore}
                onRestart={handleRestart}
                onContinue={handleContinue}
                onGoHome={() => setGameStatus(GAME_STATUS?.IDLE)}
                onOpenShop={() => setShowShop(true)}
                completedMissions={completedMissions}
              />
            )}

            {/* Shop overlay */}
            {showShop && <ShopScreen onClose={() => setShowShop(false)} />}

            {/* Missions overlay */}
            {showMissions && (
              <MissionsScreen
                onClose={() => setShowMissions(false)}
                currentMissions={missions}
              />
            )}

            {/* Leaderboard overlay */}
            {showLeaderboard && <LeaderboardScreen onClose={() => setShowLeaderboard(false)} />}
          </div>

          {/* Mobile controls */}
          {(isPlaying || isPaused) && (
            <MobileControls
              onLeft={() => canvasInputRef?.current?.triggerLeft?.()}
              onRight={() => canvasInputRef?.current?.triggerRight?.()}
              onJump={() => canvasInputRef?.current?.triggerJump?.()}
              onSlide={() => canvasInputRef?.current?.triggerSlide?.()}
            />
          )}

          {/* Controls guide */}
          {isIdle && (
            <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
              <h3 className="font-heading font-semibold text-sm mb-3" style={{ color: 'var(--color-foreground)' }}>🎮 Controls</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: '⌨️', title: 'Keyboard', desc: '← → to lane switch, Space to jump, ↓ to slide' },
                  { icon: '📱', title: 'Mobile', desc: 'Swipe left/right to switch, swipe up to jump, swipe down to slide' },
                  { icon: '🎮', title: 'Controller', desc: 'Left stick to move, A to jump, B to slide' },
                  { icon: '🖱️', title: 'Touch', desc: 'Tap the on-screen buttons below the game' },
                ]?.map(ctrl => (
                  <div key={ctrl?.title} className="p-3 rounded-lg" style={{ background: 'var(--color-muted)' }}>
                    <div className="text-xl mb-1">{ctrl?.icon}</div>
                    <p className="font-body text-xs font-semibold mb-0.5" style={{ color: 'var(--color-foreground)' }}>{ctrl?.title}</p>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{ctrl?.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Game info */}
          {isIdle && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { emoji: '🌍', title: '5 Worlds', desc: 'Jungle, City, Beach, Snow, Space' },
                { emoji: '🐒', title: '5 Characters', desc: 'Classic, Ninja, Pirate, Golden, Space' },
                { emoji: '🛵', title: '5 Vehicles', desc: 'Scooter, Skateboard, Hoverboard, Jeep, Rocket' },
                { emoji: '⚡', title: '5 Power-Ups', desc: 'Magnet, Shield, Multiplier, Jetpack, Speed' },
              ]?.map(info => (
                <div
                  key={info?.title}
                  className="p-4 rounded-xl text-center"
                  style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
                >
                  <div className="text-2xl mb-1">{info?.emoji}</div>
                  <p className="font-body text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>{info?.title}</p>
                  <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>{info?.desc}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
